import { formatMultipleSortingForEndpoint } from '@app/components/data-table/lib/parsers';
import Page from '@app/components/layout/page/page';
import { useDataTableQueryState } from '@app/hooks/use-data-table-query-state';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { useGetMobileCombustionActivityList } from '@shared/api/hooks/ccf/activities/mobile-combustion';
import Text from '@shared/components/content/text';
import TextList from '@shared/components/content/text-list';
import { useCCF } from '../../ccf-context';
import MobileCombustionEditForm from '../../components/scope-1/mobile-combustion-edit-form';
import MobileCombustionTable from '../../components/scope-1/mobile-combustion-table';
import { reportSlugs } from '../../slugs';
import { useCategory } from '../../utils';
import EmptyCategoryPage from '../ccf-category-intro-page';

export default function MobileCombustionPage() {
  const { uuid: reportUuid } = useCCF();
  const category = useCategory(reportSlugs.mobileCombustion);
  const {
    showSimpleDialog: showMobileCombustionDialog,
    SimpleDialogComponent: MobileCombustionDialog,
    closeSimpleDialog: closeMobileCombustionDialog,
  } = useSimpleDialog();

  const { page, perPage, period, site, sort, supplier, fuel } =
    useDataTableQueryState({
      filters: ['supplier', 'period', 'site', 'fuel'],
    });

  const {
    data,
    isPreviousData,
    isFetchedAfterMount,
    isLoading,
    isFetching,
    status,
  } = useGetMobileCombustionActivityList(reportUuid!, {
    page,
    page_size: perPage,
    suppliers: supplier || undefined,
    time_periods: period || undefined,
    sites: site || undefined,
    fuels: fuel || undefined,
    sort: formatMultipleSortingForEndpoint(sort! || []),
  });

  const description = (
    <>
      <Text>
        Mobile combustion is the burning of fuels such as diesel, petrol or
        biodiesel in vehicles your company has{' '}
        <span className="font-semibold">operational control</span> over. This
        means your company pays for the fuel, whether the vehicles are owned or
        leased long-term.
      </Text>
      <Text className="mt-3">
        For electric vehicles your company operates, if they are primarily
        charged at your premises, their emissions are already captured in your
        purchased electricity (Scope 2).
      </Text>
      <Text className="mt-3">Examples include:</Text>
      <TextList>
        <li>
          <span className="font-semibold">Delivery trucks</span> transporting
          goods between sites.
        </li>
        <li>
          <span className="font-semibold">Forklifts</span> operating in
          warehouses where you have operational control.
        </li>
        <li>
          <span className="font-semibold">Company cars</span> used for travel or
          logistics.
        </li>
      </TextList>
    </>
  );

  return (
    <Page name="Scope 1 - Mobile combustion" page="subpage">
      {category.hasActivityData ? (
        <MobileCombustionTable
          data={data!}
          status={status}
          isLoading={isLoading}
          isFetching={isFetching}
          isPreviousData={isPreviousData}
          isFetchedAfterMount={isFetchedAfterMount}
        />
      ) : (
        <EmptyCategoryPage
          categorySlug="mobile-combustion"
          description={description}
          primaryActionProps={{
            onClick: showMobileCombustionDialog,
          }}
        />
      )}
      {MobileCombustionDialog({
        header: 'Add mobile combustion data',
        content: (
          <MobileCombustionEditForm closeDialog={closeMobileCombustionDialog} />
        ),
      })}
    </Page>
  );
}
